/* pamela-border-registry.js
   Border Overlay Manifest Loader (global)

   Exposes:
     window.PAMELA_GET_BORDER_OVERLAY_URL(handle, variantOrObj)
       -> returns a URL string (or "" if none)

   Manifest format expected (your Step 1 JSON):
   {
     "version": 1,
     "note": "...",
     "items": [
       { "handle": "...", "variantId": "...", "variantMatch": "...", "url": "..." }
     ]
   }
*/

(function () {
  "use strict";

  const MANIFEST_URL = (window.PAMELA_BORDER_MANIFEST_URL || "").trim();
  const CACHE_KEY = "__pamelaBorderManifest";
  const CACHE_AT_KEY = "__pamelaBorderManifestAt";
  const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

  function safeStr(v) {
    return typeof v === "string" ? v : v == null ? "" : String(v);
  }

  function getVariantId(v) {
    if (!v) return "";
    // Shopify product JSON variants usually have: id (number)
    // Sometimes you may pass { id, title } etc.
    return safeStr(v.id || v.variantId || v.variant_id || "");
  }

  function getVariantTitle(v) {
    if (!v) return "";
    return safeStr(v.title || v.name || "");
  }

  async function fetchManifest() {
    const now = Date.now();
    const cached = window[CACHE_KEY];
    const cachedAt = window[CACHE_AT_KEY] || 0;

    if (cached && now - cachedAt < CACHE_TTL_MS) return cached;

    if (!MANIFEST_URL) {
      console.warn("[BorderManifest] Missing window.PAMELA_BORDER_MANIFEST_URL");
      window[CACHE_KEY] = { items: [] };
      window[CACHE_AT_KEY] = now;
      return window[CACHE_KEY];
    }

    const res = await fetch(MANIFEST_URL, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) throw new Error("Border manifest fetch failed: " + res.status);

    const json = await res.json();
    // Normalize items array
    const items = Array.isArray(json?.items) ? json.items : Array.isArray(json) ? json : [];
    const norm = {
      version: json?.version ?? 1,
      note: json?.note ?? "",
      items,
    };

    window[CACHE_KEY] = norm;
    window[CACHE_AT_KEY] = now;
    return norm;
  }

  function normalizeHandle(h) {
    return safeStr(h).trim().toLowerCase();
  }

  function normalizeId(id) {
    return safeStr(id).trim();
  }

  function includesLoose(haystack, needle) {
    const h = safeStr(haystack).toLowerCase();
    const n = safeStr(needle).toLowerCase();
    if (!h || !n) return false;
    return h.includes(n);
  }

async function getBorderUrl(handle, variantObj) {
  try {
    const h = normalizeHandle(handle);
    const vid = normalizeId(getVariantId(variantObj));
    const vtitle = safeStr(getVariantTitle(variantObj));

    // Use globals from boot() if available (helps auto-build)
    const productTitle = safeStr(window.__PAMELA_PRODUCT_TITLE__ || "");
    const selectedVariant = window.__PAMELA_SELECTED_VARIANT__ || null;

    // --------------------------
    // 1) Manifest lookup (existing behavior)
    // --------------------------
    if (h) {
      const manifest = await fetchManifest();
      const items = Array.isArray(manifest.items) ? manifest.items : [];

      // 1a) EXACT MATCH: handle + variantId
      if (vid) {
        const exact = items.find((it) => {
          return normalizeHandle(it?.handle) === h && normalizeId(it?.variantId) === vid;
        });
        if (exact && exact.url) return safeStr(exact.url).trim();
      }

      // 1b) FALLBACK: handle + variantMatch substring in title
      const fallback = items.find((it) => {
        if (normalizeHandle(it?.handle) !== h) return false;
        const match = safeStr(it?.variantMatch).trim();
        if (!match) return false;
        return includesLoose(vtitle, match);
      });
      if (fallback && fallback.url) return safeStr(fallback.url).trim();
    }

    // --------------------------
    // 2) Auto-build URL (scalable, no giant manifest)
    // --------------------------
    // Base folder where you store Files
    const BASE = "https://cdn.shopify.com/s/files/1/0721/2564/5027/files/";

    // Color comes from variant title: "Georgia Gray" -> "GeorgiaGray"
    const colorSrc = safeStr(vtitle || selectedVariant?.title || "").trim();
    const color = colorSrc.replace(/[^A-Za-z0-9]+/g, "").trim();
    if (!color) return "";

    // Sizes come from product title like:
    // "Companion Upright Monument 48x6x24 on base 72x12x6 with Med Vases"
    const t = safeStr(productTitle).toLowerCase();

    function pickTabletSize(titleLower) {
      // find first occurrence like 48x6x24 (width x thickness x height)
      const m = titleLower.match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/i);
      if (!m) return null;
      const w = m[1], h = m[3];
      return `${w}x${h}`; // 48x24
    }

    function pickBaseSize(titleLower) {
      // find "base 72x12x6" -> 72x6
      const m = titleLower.match(/base\s+(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/i);
      if (!m) return null;
      const w = m[1], h = m[3];
      return `${w}x${h}`; // 72x6
    }

    const tablet = pickTabletSize(t);
    const base = pickBaseSize(t);
    if (!tablet || !base) return "";

    // Optional vase suffix (DO NOT turn on until you actually name files with it)
    // Set window.PAMELA_BORDER_USE_WVASE = true later if you want "_WVASE" filenames.
    const wantsWVase = !!window.PAMELA_BORDER_USE_WVASE && (t.includes("vase") || t.includes("vases"));
    const vaseSuffix = wantsWVase ? "_WVASE" : "";

    const filename = `${color}_${tablet}_${base}_Border${vaseSuffix}.png`;

    // Optional cache-bust you can set from theme if needed:
    // window.PAMELA_BORDER_URL_SUFFIX = "?v=123"
    const suffix = safeStr(window.PAMELA_BORDER_URL_SUFFIX || "");

    return (BASE + filename + suffix).trim();
  } catch (e) {
    console.error("[PAMELA_GET_BORDER_OVERLAY_URL] Failed:", e);
    return "";
  }
}

  // Expose API
  window.PAMELA_GET_BORDER_OVERLAY_URL = getBorderUrl;

  // Optional: quick sanity log (won't break anything)
  try {
    console.log("[BorderRegistry] Ready. Manifest URL =", MANIFEST_URL || "(missing)");
  } catch (_) {}
})();
